import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import CrudForm from "../../components/common/crud/CrudForm";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import alertService from "../../utils/alertService";
import { formatDate } from "../../utils/dateUtils";

const lotFormSchema = [
  {
    name: "lotNo",
    label: "Lot Number",
    type: "text",
    placeholder: "e.g. LOT-001",
    validation: {
      required: true,
      minLength: 2,
      maxLength: 100,
    },
  },
  
  {
    name: "isActive",
    label: "Active",
    type: "checkbox",
    initialValue: true,
  },
];

function LotModulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();
  const [editValues, setEditValues] = useState(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);

  const isCreatePage = location.pathname.endsWith("/new");
  const isEditPage =
    Boolean(id) &&
    (location.pathname.endsWith("/edit") ||
      location.pathname.includes(`/${id}`));
  const isFormPage = isCreatePage || isEditPage;
  const formMode = isEditPage ? "edit" : "create";

  // Base path for navigation within this module
  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/lot"))
      return "/settings-master/lot";
    return "/lot";
  }, [location.pathname]);

  const endpoints = useMemo(
    () => ({
      list: "/lot",
      create: "/lot",
      edit: (id) => `/lot/${id}`,
      update: (id) => `/lot/${id}`,
      delete: (id) => `/lot/${id}`,
    }),
    [],
  );

  const {
    rows,
    isLoading,
    isSubmitting,
    error,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "lotId",
    updateMethod: "patch",
    initialFilters: { search: "" },
    initialSort: { key: "lotNo", order: "ASC" },
  });

  const tableColumns = useMemo(
    () => [
      {
        key: "lotNo",
        label: "Lot Number",
        sortable: true,
        render: (item) => String(item?.lotNo || ""),
      },
      {
        key: "isActive",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        key: "excessWeight",
        label: "Excess Weight",
        sortable: false,
        render: (item) => (
          <span className="font-mono text-emerald-600 dark:text-emerald-400">
            {Number(item?.excessWeight || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
            })}{" "}
            KG
          </span>
        ),
      },
      {
        key: "wastageWeight",
        label: "Wastage Weight",
        sortable: false,
        render: (item) => (
          <span className="font-mono text-rose-600 dark:text-rose-400">
            {Number(item?.wastageWeight || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
            })}{" "}
            KG
          </span>
        ),
      },
      {
        key: "isLocked",
        label: "Lot Locked",
        sortable: false,
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isLocked
                ? "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isLocked ? "Locked" : "Unlocked"}
          </span>
        ),
      },
      {
        key: "createdDate",
        label: "Created Date",
        sortable: true,
        render: (item) => formatDate(item?.createdDate),
      },
    ],
    [],
  );

  useEffect(() => {
    if (!isEditPage || !id) {
      setEditValues(null);
      setFormError(null);
      return;
    }

    let isMounted = true;
    const loadEditData = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/lot/${id}`);
        const payload = response?.data || {};
        const data = payload?.data || payload || {};
        if (!isMounted) return;
        setEditValues(data);
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditData();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  const handleSave = async (values) => {
    setFormError(null);
    try {
      if (formMode === "edit" && id) {
        await apiClient.patch(`/lot/${id}`, values);
        alertService.success("Record updated successfully.");
      } else {
        await apiClient.post("/lot", values);
        alertService.success("Record created successfully.");
      }
      await refresh();
      navigate(basePath, { replace: true });
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    }
  };

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) =>
    navigate(`${basePath}/${encrypt(item?.lotId)}/edit`);

  if (isFormPage) {
    const notFound =
      formMode === "edit" && !isLoadingEdit && !editValues && !formError;

    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => navigate(basePath)}>
            Back to List
          </Button>
        </div>

        {formError ? <ErrorAlert message={formError} /> : null}
        {notFound ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Record not found.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <CrudForm
              mode={formMode}
              schema={lotFormSchema}
              initialValues={
                formMode === "edit" ? editValues || {} : { isActive: true }
              }
              isSubmitting={isSubmitting || isLoadingEdit}
              onSubmit={handleSave}
              onCancel={() => navigate(basePath)}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={askDelete}
        searchKey="search"
        searchPlaceholder="Search by lot number"
        tableScrollHeight="52vh"
        toolbarAction={<Button onClick={openCreate}>Create Lot</Button>}
      />
    </div>
  );
}

export default LotModulePage;
